import React from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {jwtDecode} from 'jwt-decode';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import {ChromePicker} from 'react-color';
import {Typography, Divider, Box, Grid, useTheme} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {CloseOutlined} from '@mui/icons-material';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {useSnackbar} from 'notistack';
import {
  BASE_APPLICATION,
  BASE_LEGACY_APP,
  BASE_RECORD_ID,
  BASE_ROUTE,
  BASE_ROUTE_MODULE,
  BASE_ROUTE_SUB_MODULE,
  BASE_ROUTE_PAD,
  IS_FROM_EPIC,
  MODE,
  PARAMS_KEY,
  IS_FROM_ATHENA
} from 'src/routes/BaseRoute';
import {APP} from 'src/styles/variables';
import AuthUtils from 'src/utils/AuthUtils';

// Components or Constants imports
import TextInput from '@components/form/text-input/text-input/TextInput';
// import TextButton from '@components/button/text-button/TextButton';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {createApplicationsSchema} from '@shared/modules/public/forms/validation/LogInValidation';
import {
  getApplicationListData,
  createNewApplication,
  getUserPermissions,
  createAuditLog
} from '@shared/services/service-handlers/PrivateService';
import {getReferenceData} from '@shared/services/service-handlers/PrivateService';

import {
  DashboardWrapperGrid,
  DashboardLayoutGrid,
  TabGrid,
  TabContainerGrid,
  IconDiv,
  TabLayoutGrid,
  TabSectionGrid,
  CenterCircularProgress,
  DialogActionsButtonStyle,
  DialogTitleStyle,
  IconButtonDiv
} from './DashboardStyles';

import EncryptUtils from 'src/utils/EncryptUtils';
import FormConstants from '@shared/constants/FormConstants';
import ActionModalDialog from '@components/dialog/action-dialog/ActionModalDialog';
import {useAppContext} from '@context/AppContext';
import {environment} from '@shared/config/environment';
import {RoleBasedGuard} from 'src/auth/guard';
import {LoadingButton} from '@mui/lab';
import {PulseLoader} from 'react-spinners';

import ComponentCard from 'src/sections/_examples/component-card';
import AccessDeniedCard from '@components/access-denied/AccessDeniedCard';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const applications = {
  data: [
    {
      id: 1,
      name: 'BytePad Demo',
      application_name: 'Bytepad_Legacy',
      description: 'Data Archival'
    },
    {
      id: 2,
      name: 'BytePad Reflect',
      application_name: null,
      description: 'Business Intelligence'
    },
    {
      id: 3,
      name: 'BytePad AppHarbor',
      application_name: 'App_Rationalization',
      description: 'Application Rationalization'
    },
    {
      id: 4,
      name: 'BytePad AR',
      application_name: null,
      description: 'AR Rundown Module'
    },
    {
      id: 5,
      name: 'AdaptCare Demo',
      application_name: 'AdaptCare_Demo',
      description: 'Patient Care Platform'
    },
    {
      id: 6,
      name: 'BytePad HCM',
      application_name: null,
      description: 'Human Capital Module'
    },
    {
      id: 7,
      name: 'BytePad SCM',
      application_name: null,
      description: 'Supply Chain Module'
    },
    {
      id: 8,
      name: 'BytePad FIN',
      application_name: null,
      description: 'Finance Module'
    },
    {
      id: 9,
      name: 'BytePad LMS',
      application_name: 'LMS',
      description: 'Learning Management'
    },
    {
      id: 10,
      name: 'BytePad Imaging',
      application_name: null,
      description: 'DICOM Image Viewer'
    }
  ]
};

const Dashboard = (): React.ReactElement => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {appState, setAppName} = useAppContext();

  const theme = useTheme();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {enqueueSnackbar} = useSnackbar();
  const {
    handleSubmit,
    control,
    formState: {errors},
    setValue,
    trigger,
    reset
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(createApplicationsSchema)
  });

  const [showUnAuthenticatedPopup, setShowUnAuthenticatedPopup] =
    React.useState<boolean>(false);
  const [email, setEmail] = React.useState<any>('');
  const [rolesErrorStatus, setRolesErrorStatus] =
    React.useState<boolean>(false);
  let userData: any;

  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
  }

  // states
  const [state, setState] = React.useState({
    tabData: [],
    apiLoader: false,
    isDisabled: false,
    open: false,
    colorHexCode: '',
    showColorPicker: false,
    loader: false
  });
  const {tabData, open, colorHexCode, loader, apiLoader} = state;

  // input fields data array
  const inputFieldsData = [
    {
      id: 1,
      name: 'application_name',
      label: t('PRIVATE.DASHBOARD.ENTER_YOUR_APPLICATION_NAME', {
        ns: 'private'
      }) // label: Enter your application name
    },
    {
      id: 2,
      name: 'application_color',
      label: t('PRIVATE.DASHBOARD.SELECT_YOUR_APPLICATION_COLOR', {
        ns: 'private'
      }) // label: Select Your application color
    }
  ];

  // functions
  const handleClose = () => {
    reset();
    setValue('application_color', '');
    setState((prevState: any) => ({
      ...prevState,
      open: false
    }));
  };
  // ----used for the adaptcare lms integration-------------
  // const enterdirect = async () => {
  //   let userPermission = AuthUtils.getUserAppPermissions();
  //   if (!userPermission) {
  //     setState((prevState: any) => ({
  //       ...prevState,
  //       loader: true
  //     }));
  //     const res = await getUserPermissions();
  //     userPermission = res?.applications_list;
  //     AuthUtils.setUserAppPermissions(res?.applications_list);
  //     setState((prevState: any) => ({
  //       ...prevState,
  //       loader: false
  //     }));
  //   }
  //   const userPermission_found = userPermission?.find(
  //     (item: string) => item === 'LMS'
  //   );
  //   if (!userPermission_found) {
  //     setShowUnAuthenticatedPopup(true);
  //     return;
  //   }
  //   setAppName('LMS');
  //   navigate(`/${BASE_ROUTE}/${BASE_ROUTE_MODULE}`);
  // };

  // functions for api calling
  const getApplicationData = async () => {
    try {
      setState(prevState => ({
        ...prevState,
        apiLoader: true
      }));
      //enterdirect();
      const data = await getApplicationListData();
      setState((prevState: any) => ({
        ...prevState,
        tabData: data,
        apiLoader: false
      }));
      setRolesErrorStatus(false);
    } catch (e: any) {
      setState((prevState: any) => ({
        ...prevState,
        apiLoader: false
      }));
      setRolesErrorStatus(true);
      // enqueueSnackbar(e.message, SnackBarConfig.getError());
    }
  };

  const onSubmit = async (data: any) => {
    try {
      const matches = data.application_name.match(/\b(\w)/g);
      const iconName = matches?.join('');
      const payload = {
        application_name: data.application_name.replace(
          /(^\w{1})|(\s+\w{1})/g,
          (letter: string) => letter.toUpperCase()
        ),
        icon_name: iconName?.toUpperCase(),
        color_hex_code: data.application_color
      };
      setState((prevState: any) => ({
        ...prevState,
        loader: true
      }));
      const res = await createNewApplication(payload);
      setState((prevState: any) => ({
        ...prevState,
        loader: false
      }));
      enqueueSnackbar(res?.message, SnackBarConfig.getSuccess());
      handleClose();
      getApplicationData();
    } catch (error: any) {
      setState((prevState: any) => ({
        ...prevState,
        loader: false
      }));
      const {message}: any = error;
      enqueueSnackbar(message, SnackBarConfig.getError());
    }
  };

  const getOrgEmail = async () => {
    const res = await getReferenceData();
    setEmail(res?.data?.email);
    setShowUnAuthenticatedPopup(true);
    AuthUtils.clearRedirectRoute();
  };

  const fetchPermissions = async () => {
    try {
      const res = await getUserPermissions();
      AuthUtils.setPermissions(res?.data);
    } catch (e) {}
  };

  React.useEffect(() => {
    AuthUtils.clearSelectedCheckBoxData();
    AuthUtils.clearSelectedCheckBoxList();
    AuthUtils.clearAdditionalSelectedCheckBoxList();
    AuthUtils.clearPatientAccountingSelectedCheckBoxList();
    AuthUtils.clearSelectedAdditionalCheckBoxData();
    AuthUtils.clearSelectedPatientAccountingCheckBoxData();
    fetchPermissions();
    if (location?.state?.isUnAuthenticatedPopup) {
      setShowUnAuthenticatedPopup(true);
      return;
    }
    if (
      userData?.user?.type === FormConstants.EPIC_TERMS.BASE &&
      AuthUtils.getEpicPatientId()
    ) {
      const paramsKey = EncryptUtils.setEncryptedParams({
        [BASE_RECORD_ID]: AuthUtils.getEpicPatientId(),
        [BASE_LEGACY_APP]: '',
        [IS_FROM_EPIC]: 'true',
        [MODE]: FormConstants.STATIC_VALUES.VIEW
      });

      const redirectUrl = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${FormConstants.MODULE.CLINICAL}/${BASE_ROUTE_SUB_MODULE}?${PARAMS_KEY}=${paramsKey}`;
      navigate(redirectUrl);
    } else if (
      userData?.user?.type === FormConstants.ATHENA_TERMS.BASE &&
      AuthUtils.getAthenaPatientId()
    ) {
      const paramsKey = EncryptUtils.setEncryptedParams({
        [BASE_RECORD_ID]: AuthUtils.getAthenaPatientId(),
        [BASE_LEGACY_APP]: '',
        [IS_FROM_ATHENA]: 'true',
        [MODE]: FormConstants.STATIC_VALUES.VIEW
      });

      const redirectUrl = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${FormConstants.MODULE.CLINICAL}/${BASE_ROUTE_SUB_MODULE}?${PARAMS_KEY}=${paramsKey}`;
      navigate(redirectUrl);
    } else {
      getApplicationData();
    }

    // if (AuthUtils.getRedirectRoute()) {
    //   getOrgEmail();
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Based on id it will redirect to the module list screen
  const handleClick = async (id: any, application_name: string) => {
    if (
      application_name === 'App_Rationalization' &&
      environment?.App_Rationalization_Link?.length
    ) {
      return (window.location.href = environment.App_Rationalization_Link);
    }
    if (application_name === 'AdaptCare_Demo') {
      window.open('https://test.adaptcare.app/', '_blank');
      return;
    }
    let userPermission = AuthUtils.getUserAppPermissions();
    if (!userPermission) {
      setState((prevState: any) => ({
        ...prevState,
        loader: true
      }));
      const res = await getUserPermissions();
      userPermission = res?.applications_list;
      AuthUtils.setUserAppPermissions(res?.applications_list);
      setState((prevState: any) => ({
        ...prevState,
        loader: false
      }));
    }
    const userPermission_found = userPermission?.find(
      (item: string) => item === application_name
    );
    if (!userPermission_found) {
      setShowUnAuthenticatedPopup(true);
      return;
    }
    setAppName(application_name);
    if (application_name) {
      const data = {
        isBH_selected: true,
        isBH_reason: '',
        application_name: application_name,
        event_description: `User accessed application : ${application_name}`,
        audit_identifier: AuthUtils.getAuditIdentifier(),
        audit_identifier_type: AuthUtils.getAuditIdentifierType(),
        audit_legacy_app: AuthUtils.getAuditLegacyApp()
      };
      createAuditLog(application_name, data);
      navigate(`/${BASE_ROUTE}/${BASE_ROUTE_MODULE}`);
    } else {
      navigate(`/${BASE_ROUTE}/${BASE_APPLICATION}/${id}/${BASE_ROUTE_PAD}`);
    }
  };

  const dialogClose = () => {
    setShowUnAuthenticatedPopup(false);
  };

  // const renderDescription = () => {
  //   return (
  //     <>
  //       <MainDescriptionTypography variant="h2">
  //         {t('PRIVATE.DASHBOARD.HEADING', {ns: 'private'})}
  //       </MainDescriptionTypography>
  //       <Typography
  //         variant="h4"
  //         sx={{
  //           mb: 2,
  //           whiteSpace: 'pre-line',
  //           alignItems: 'center',
  //           textAlign: 'center'
  //         }}>
  //         {t('PRIVATE.DASHBOARD.SUB_HEADING', {ns: 'private'})}
  //       </Typography>
  //     </>
  //   );
  // };

  const renderCreateButton = () => {
    return (
      <LoadingButton
        type="submit"
        loading={loader}
        variant="contained"
        sx={{
          fontWeight: 'normal',
          backgroundColor: `${APP.PALETTE.MODULE_LAYOUT_COLOR.BUTTON}`,
          '&:hover': {
            backgroundColor: `${APP.PALETTE.MODULE_LAYOUT_COLOR.HOVER_BUTTON}`
          }
        }}>
        {t('PRIVATE.COMMON.CREATE', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const handleClickOpen = () => {
    setState((prevState: any) => ({
      ...prevState,
      open: true
    }));
  };

  const handleChangeValue = (e: any) => {
    e.preventDefault();
    setState((prevState: any) => ({
      ...prevState,
      applicationName: e.target.value
    }));
  };

  const closeColorPicker = (): void => {
    setState(prevState => ({
      ...prevState,
      showColorPicker: false
    }));
  };

  const handleColorChange = (color: any): void => {
    setState(prevState => ({
      ...prevState,
      colorHexCode: color?.hex
    }));
    setValue('application_color', color?.hex);
    trigger();
  };

  const openColorPicker = (): void => {
    setState(prevState => ({
      ...prevState,
      showColorPicker: true
    }));
  };

  const renderColorPicker = (): React.ReactElement => {
    return (
      <div
        style={{
          zIndex: 2,
          position: 'relative'
        }}>
        <div
          style={{
            position: 'absolute',
            left: '14rem',
            cursor: 'pointer'
          }}
          onClick={closeColorPicker}>
          <CloseOutlined />
        </div>
        <ChromePicker
          disableAlpha={true}
          onChange={handleColorChange}
          color={colorHexCode}
        />
      </div>
    );
  };

  const renderApplicationDialog = () => {
    return (
      <Dialog
        fullWidth
        open={open}
        onClose={() => handleClose()}
        style={{borderRadius: '0.5'}}>
        <DialogTitleStyle>
          {t('PRIVATE.DASHBOARD.ENTER_APPLICATION_NAME', {
            ns: 'private'
          })}
          <Box>
            <IconButtonDiv onClick={() => handleClose()}>
              <CloseOutlined />
            </IconButtonDiv>
          </Box>
        </DialogTitleStyle>
        <Divider
          style={{color: APP.PALETTE.GREY.LIGHT_GRAY, marginBottom: 20}}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            {inputFieldsData.map((item: any) => {
              return (
                <Grid item xs={12} key={item.name}>
                  <Controller
                    key={item.id}
                    name={item.name}
                    control={control}
                    render={({
                      field: {value, onChange},
                      fieldState: {error}
                    }) => {
                      return (
                        <TextInput
                          style={{width: '100%', marginBottom: '1rem'}}
                          // autoFocus
                          onChange={(event: any) => {
                            onChange(event);
                            item.name === 'application_name'
                              ? handleChangeValue(event)
                              : openColorPicker();
                          }}
                          value={value}
                          name={item.name}
                          placeholder={item.label}
                          label={item.label}
                          error={!!errors[item.name]}
                          helperText={t(
                            `${
                              errors?.[item.name]
                                ? errors?.[item.name]?.message || ''
                                : ''
                            }`
                          )}
                          fullWidth
                          variant="outlined"
                        />
                      );
                    }}
                  />
                </Grid>
              );
            })}
            <div> {state.showColorPicker && renderColorPicker()}</div>
          </DialogContent>
          <DialogActionsButtonStyle>
            {renderCreateButton()}
          </DialogActionsButtonStyle>
        </form>
      </Dialog>
    );
  };

  const disabledState = () => {
    setState((prevState: any) => ({
      ...prevState,
      isDisabled: true
    }));
  };

  React.useEffect(() => {
    disabledState();
  }, []);

  const renderStaticTabs = () => {
    if (environment?.ENABLE_CREATE_POD_FROM_SCRATCH) {
      return (
        <TabContainerGrid
          item
          onClick={() => handleClickOpen()}
          style={{
            border: '1px dashed' + '' + `${APP.PALETTE.TABS.DARK}`,
            cursor: 'pointer'
          }}>
          <IconDiv>
            <AddCircleOutlineIcon
              sx={{
                fontSize: '50px',
                // color: `${APP.PALETTE.TABS.DARK}`
                color: 'info.dark'
              }}
            />
          </IconDiv>
          <Typography
            variant="h4"
            sx={{whiteSpace: 'pre-line', fontWeight: '900'}}>
            {t('PRIVATE.DASHBOARD.CREATE_FROM_STARCH', {ns: 'private'})}
          </Typography>
        </TabContainerGrid>
      );
    }
  };

  const renderDynamicTabs = (pods: any) => {
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '70vw',
          height: '80vh',
          padding: '3rem',
          mt: 4,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingBottom: '1rem',
          scrollBehavior: 'smooth',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
        <Grid container spacing={3} justifyContent="flex-start">
          {pods?.data?.map((app: any) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              xl={2.4}
              key={app.id}
              onClick={() => handleClick(app.id, app.application_name)}>
              <ComponentCard item={app} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // const renderDynamicTabs = (value: any) => {
  //   return value?.data?.map((item: any) => {
  //     return (
  //       <TabContainerGrid
  //         item
  //         borderColor={APP.PALETTE.TABS.GRAY}
  //         color={item?.color || APP.PALETTE.GREY.LIGHT_GRAY}
  //         key={item.id}
  //         onClick={() => handleClick(item.id, item.application_name)}
  //         sx={{
  //           '&:hover': {cursor: 'pointer'}
  //         }}>
  //         <IconDiv>
  //           <AvatarInputDiv
  //             src={''}
  //             alt={''}
  //             variant={'rounded'}
  //             color={item?.color}>
  //             <h4>{item.icon_text}</h4>
  //           </AvatarInputDiv>
  //         </IconDiv>
  //         <TabNameTypography>{item.name}</TabNameTypography>
  //       </TabContainerGrid>
  //     );
  //   });
  // };

  const renderTabs = () => {
    return (
      <TabSectionGrid container>
        {renderStaticTabs()}
        {renderDynamicTabs(tabData)}
      </TabSectionGrid>
    );
  };

  const renderDialog = (dialogProps: any): React.ReactElement => {
    return <ActionModalDialog {...dialogProps} />;
  };

  // const getWelcomeBackUserName = () => {
  //   if (userData?.user?.name) return userData?.user?.name;
  //   else if (userData?.user?.first_name && userData?.user?.last_name)
  //     return userData?.user?.first_name + ' ' + userData?.user?.last_name;
  //   else return userData?.name;
  // };

  if (!apiLoader && rolesErrorStatus) {
    return <AccessDeniedCard message={environment?.USER_ROLES_ERROR_MESSAGE} />;
  }
  return !apiLoader ? (
    <DashboardWrapperGrid>
      <DashboardLayoutGrid>
        <TabGrid>
          <TabLayoutGrid>{renderTabs()}</TabLayoutGrid>
        </TabGrid>
      </DashboardLayoutGrid>

      {open && renderApplicationDialog()}

      {showUnAuthenticatedPopup &&
        renderDialog({
          open: showUnAuthenticatedPopup,
          onClose: dialogClose,
          isUnAuthenticatedPopup: true,
          title: '',
          description: (
            <RoleBasedGuard
              email={email}
              hasContent={true}
              isAuthenticated={showUnAuthenticatedPopup}
              sx={{mt: 5}}
            />
          ),
          // email ? (
          //   <div>
          //     <Typography>
          //       {t('You do not have permission to access this page!', {
          //         ns: 'private'
          //       })}
          //     </Typography>
          //     <Typography>
          //       To request permission please email to{' '}
          //       <a href={`mailto:${email}`}>{email}</a>
          //     </Typography>
          //   </div>
          // ) : (
          //   <>
          //     <RoleBasedGuard
          //       email={email}
          //       hasContent={true}
          //       isAuthenticated={showUnAuthenticatedPopup}
          //       sx={{py: 10}}
          //     />
          //     <div>
          //       <Typography>
          //         {t('You do not have permission to access this page!', {
          //           ns: 'private'
          //         })}
          //       </Typography>
          //       <Typography>
          //         {t('Please contact your IT department.', {ns: 'private'})}
          //       </Typography>
          //     </div>
          //   </>
          // ),
          noLabelText: `OK`,
          handleCloseBtnClick: dialogClose
        })}
    </DashboardWrapperGrid>
  ) : (
    <CenterCircularProgress>
      <PulseLoader color={theme.palette.primary.main} />
    </CenterCircularProgress>
  );
};

export default Dashboard;

